import { DAY_END_MINUTES, DAY_START_MINUTES, SLOT_MINUTES, minutesToTime, slotIndex, slotSpan, timeToMinutes } from './lobbyScheduleUtils'
import type { ScheduleMeetingDto } from '../../../types/lobbyDay'

export function barGeometry(meeting: ScheduleMeetingDto): { startSlot: number; span: number } {
  return { startSlot: slotIndex(meeting.startTime), span: slotSpan(meeting.startTime, meeting.endTime) }
}

export function clampEndToDay(startTime: string, durationMin: number): string {
  const start = timeToMinutes(startTime)
  const capped = Math.min(start + durationMin, DAY_END_MINUTES)
  return minutesToTime(Math.max(capped, start + SLOT_MINUTES))
}

export const MIN_CARD_HEIGHT_PX = 34
export const MIN_COL_PX = 116

/** Absolute position of a card on the day canvas. `pxPerMin` = SLOT_PX / SLOT_MINUTES. */
export function cardGeometry(meeting: ScheduleMeetingDto, pxPerMin: number): { top: number; height: number } {
  const start = timeToMinutes(meeting.startTime)
  const end = timeToMinutes(meeting.endTime)
  const top = (start - DAY_START_MINUTES) * pxPerMin
  const height = Math.max(MIN_CARD_HEIGHT_PX, (end - start) * pxPerMin)
  return { top, height }
}

export type CardLayout =
  | { mode: 'vertical'; tier: 'full' | 'condensed' | 'minimal' }
  | { mode: 'split' }

/** Below these a card can't stack its detail vertically but is wide enough for a second column. */
export const SPLIT_MIN_WIDTH_PX = 240
export const SPLIT_MAX_HEIGHT_PX = 88

/** Choose a card's content layout from its box geometry. When too short to stack vertically but
 *  wide enough, use 'split' (name/time left, room/ask/attendees flow right into dead space);
 *  otherwise fall back to the vertical density tiers. */
export function cardLayout({ widthPx, heightPx, durationMin }: {
  widthPx: number; heightPx: number; durationMin: number
}): CardLayout {
  if (widthPx >= SPLIT_MIN_WIDTH_PX && heightPx < SPLIT_MAX_HEIGHT_PX) return { mode: 'split' }
  const tier: 'full' | 'condensed' | 'minimal' =
    durationMin <= 30 ? 'minimal'
      : widthPx >= 150 && durationMin >= 45 ? 'full'
        : 'condensed'
  return { mode: 'vertical', tier }
}

/** Gap between attendee chips, and the reserved width of the trailing "+N" overflow pill. */
export const CHIP_STRIP_GAP_PX = 4
export const CHIP_OVERFLOW_PILL_PX = 34

/** How many attendee chips fit on a single row before overflowing into a "+N" pill.
 *  Given each chip's measured pixel width and the available row width, returns the count
 *  to render (the rest collapse into the pill). When all fit, no pill is needed and every
 *  chip shows. `availPx <= 0` means "not measured yet" (jsdom / pre-layout) — show them all.
 *  When trimming, room is reserved for a gap + the pill so the last visible chip never
 *  collides with it. May return 0 if not even one chip fits alongside the pill. */
export function fitChipCount(
  widths: number[],
  availPx: number,
  pillPx = CHIP_OVERFLOW_PILL_PX,
  gapPx = CHIP_STRIP_GAP_PX,
): number {
  if (availPx <= 0) return widths.length
  let total = 0
  for (let i = 0; i < widths.length; i++) total += widths[i] + (i > 0 ? gapPx : 0)
  if (total <= availPx) return widths.length
  let used = 0
  let count = 0
  for (let i = 0; i < widths.length; i++) {
    const w = widths[i] + (i > 0 ? gapPx : 0)
    if (used + w + gapPx + pillPx > availPx) break
    used += w
    count++
  }
  return count
}

/** Lane pixel width: fill the container, but never let the busiest cluster's columns
 *  fall below MIN_COL_PX — past that the lane scrolls horizontally inside its box. */
export function laneWidthPx(maxCols: number, containerWidth: number, minColPx = MIN_COL_PX): number {
  return Math.max(containerWidth, Math.max(1, maxCols) * minColPx)
}

export interface ColumnAssignment { colIndex: number; colCount: number }

const STICKY_BIG = Number.MAX_SAFE_INTEGER

/** Overlap-column layout that keeps each meeting's left-right ORDER stable across edits.
 *  Within an overlap cluster, meetings are ordered by (priorColIndex, start, end, id), then
 *  greedily packed into the lowest free column (0..k, no gaps). Removes gratuitous column
 *  renumbering from a resize while keeping widths sensible; a genuine same-column time-collision
 *  still bumps the colliding meeting. colCount per cluster = number of columns used. */
export function assignStickyColumns(
  meetings: ScheduleMeetingDto[],
  prev: Map<string, number>,
): Map<string, ColumnAssignment> {
  const items = meetings.map((m) => ({
    id: m.id, start: timeToMinutes(m.startTime), end: timeToMinutes(m.endTime),
  }))
  const byStart = [...items].sort((a, b) => a.start - b.start || a.end - b.end)
  const overlaps = (a: { start: number; end: number }, b: { start: number; end: number }) =>
    a.start < b.end && b.start < a.end

  const result = new Map<string, ColumnAssignment>()
  let cluster: typeof items = []
  let clusterMaxEnd = -Infinity

  const flush = () => {
    if (cluster.length === 0) return
    const ordered = [...cluster].sort((a, b) =>
      (prev.get(a.id) ?? STICKY_BIG) - (prev.get(b.id) ?? STICKY_BIG)
      || a.start - b.start || a.end - b.end
      || (a.id < b.id ? -1 : a.id > b.id ? 1 : 0))
    const columns: { start: number; end: number }[][] = []
    const colOf = new Map<string, number>()
    for (const it of ordered) {
      let col = 0
      while (col < columns.length && columns[col].some((p) => overlaps(p, it))) col++
      if (col === columns.length) columns.push([])
      columns[col].push({ start: it.start, end: it.end })
      colOf.set(it.id, col)
    }
    const colCount = columns.length
    for (const it of cluster) result.set(it.id, { colIndex: colOf.get(it.id)!, colCount })
    cluster = []
    clusterMaxEnd = -Infinity
  }

  for (const it of byStart) {
    if (cluster.length && it.start >= clusterMaxEnd) flush()
    cluster.push(it)
    clusterMaxEnd = Math.max(clusterMaxEnd, it.end)
  }
  flush()
  return result
}
